import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Package, MapPin, Phone, Mail, Download, Home } from 'lucide-react';
import { getOrders } from '../data/cart';

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const orders = getOrders();
    const foundOrder = orders.find(o => o.id === orderId);
    
    if (!foundOrder) {
      navigate('/shop');
      return;
    }
    
    setOrder(foundOrder);
  }, [orderId, navigate]);

  if (!order) {
    return <div className="min-h-screen flex items-center justify-center">
      <p>Loading...</p>
    </div>;
  }

  const deliveryDate = new Date(order.createdAt);
  deliveryDate.setDate(deliveryDate.getDate() + 7);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Success Header */}
      <section className="bg-gradient-to-r from-green-500 to-teal-500 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CheckCircle2 className="h-20 w-20 mx-auto mb-6 animate-bounce" />
          <h1 className="text-5xl font-bold mb-4">Order Confirmed!</h1>
          <p className="text-xl text-green-50 mb-6">
            Thank you for your purchase. Your order has been successfully placed.
          </p>
          <Badge className="bg-white text-green-600 text-lg px-6 py-2">
            Order ID: {order.id}
          </Badge>
        </div>
      </section>

      {/* Order Details */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Delivery Information */}
            <Card className="border-2 border-cyan-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-cyan-600" />
                  Delivery Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Estimated Delivery</p>
                  <p className="font-semibold text-gray-900">
                    {deliveryDate.toLocaleDateString('en-IN', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Shipping Address</p>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-500 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">{order.customer.fullName}</p>
                      <p className="text-sm text-gray-700">{order.customer.address}</p>
                      <p className="text-sm text-gray-700">
                        {order.customer.city}, {order.customer.state} - {order.customer.pincode}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="border-2 border-teal-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-teal-600" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-700">{order.customer.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-700">{order.customer.phone}</span>
                </div>
                <div className="pt-3 border-t">
                  <p className="text-sm text-gray-600">
                    Order confirmation has been sent to your email address.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Items */}
          <Card className="border-2 mb-8">
            <CardHeader>
              <CardTitle>Order Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.items.map((item) => (
                  <div key={item.id} className="flex items-start gap-4 pb-4 border-b last:border-b-0">
                    <img 
                      src={item.image}
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded border-2"
                    />
                    <div className="flex-grow">
                      <Badge className="mb-2 bg-cyan-100 text-cyan-700 border-0">
                        {item.category}
                      </Badge>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.name}</h3>
                      <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                      {item.addons && item.addons.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 font-medium">Add-ons:</p>
                          {item.addons.map((addon, idx) => (
                            <p key={idx} className="text-xs text-gray-600">• {addon.name}</p>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        ₹{((item.price * item.quantity) + item.addons.reduce((sum, a) => sum + a.price, 0)).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Payment Summary */}
          <Card className="border-2 border-green-200 bg-gradient-to-br from-white to-green-50">
            <CardHeader>
              <CardTitle>Payment Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-gray-700">
                  <span>Subtotal:</span>
                  <span className="font-semibold">₹{order.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-gray-700">
                  <span>GST (18%):</span>
                  <span className="font-semibold">₹{order.gst.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-green-600">
                  <span>Shipping:</span>
                  <span className="font-semibold">FREE</span>
                </div>
                <div className="border-t pt-3 flex items-center justify-between">
                  <span className="text-xl font-bold text-gray-900">Total Paid:</span>
                  <span className="text-3xl font-bold text-green-600">
                    ₹{order.total.toLocaleString()}
                  </span>
                </div>
                <div className="pt-2">
                  <Badge className="bg-green-500 text-white">
                    Payment Successful
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Button 
              onClick={() => navigate('/')}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              <Home className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
            <Button 
              onClick={() => navigate('/shop')}
              variant="outline"
              className="flex-1 border-2 border-cyan-500 text-cyan-600"
            >
              Continue Shopping
            </Button>
            <Button 
              variant="outline"
              className="flex-1 border-2 border-gray-300"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Invoice
            </Button>
          </div>

          {/* Next Steps */}
          <Card className="border-2 border-cyan-200 mt-8">
            <CardHeader>
              <CardTitle>What Happens Next?</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-cyan-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                    1
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Order Confirmation</p>
                    <p className="text-sm text-gray-600">You'll receive an email with your order details</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-cyan-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                    2
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Installation Scheduling</p>
                    <p className="text-sm text-gray-600">Our team will contact you within 24 hours to schedule installation</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-cyan-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                    3
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Delivery & Installation</p>
                    <p className="text-sm text-gray-600">Professional installation team will deliver and set up your RO system</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-cyan-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                    4
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">App Setup & Training</p>
                    <p className="text-sm text-gray-600">Learn how to use the Niloman HX app for real-time monitoring</p>
                  </div>
                </li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default OrderConfirmation;
